import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notifications as NotificationIcon,
  NotificationsOff as NotificationOffIcon
} from '@mui/icons-material';

const AlertsPanel = ({ 
  alerts = [], 
  title = "התראות מערכת",
  maxVisible = 5,
  showTimestamp = true,
  collapsible = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const getAlertIcon = (type) => {
    const iconProps = { fontSize: isMobile ? 'small' : 'medium' };
    
    switch (type) {
      case 'error':
        return <ErrorIcon color="error" {...iconProps} />;
      case 'warning':
        return <WarningIcon color="warning" {...iconProps} />;
      case 'info':
        return <InfoIcon color="info" {...iconProps} />;
      case 'success':
        return <SuccessIcon color="success" {...iconProps} />;
      default:
        return <InfoIcon color="info" {...iconProps} />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'success':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'עכשיו';
    if (diffMinutes < 60) return `לפני ${diffMinutes} דקות`;
    if (diffMinutes < 1440) return `לפני ${Math.floor(diffMinutes / 60)} שעות`;
    return date.toLocaleDateString('he-IL');
  };

  const getSeverityCount = () => {
    return {
      error: alerts.filter(a => a.type === 'error').length,
      warning: alerts.filter(a => a.type === 'warning').length,
      info: alerts.filter(a => a.type === 'info').length,
      success: alerts.filter(a => a.type === 'success').length
    };
  };

  const severityCount = getSeverityCount();
  const criticalCount = severityCount.error + severityCount.warning;
  const visibleAlerts = showAll ? alerts : alerts.slice(0, maxVisible);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={criticalCount} color="error" max={99}>
              <NotificationIcon />
            </Badge>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {title}
            </Typography>
          </Box>
        }
        action={
          collapsible && (
            <IconButton
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'כווץ' : 'הרחב'}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )
        }
        sx={{ pb: 1 }}
      />
      
      <Collapse in={expanded}>
        <CardContent sx={{ flex: 1, pt: 0 }}>
          {/* סיכום התראות */}
          {alerts.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {severityCount.error > 0 && (
                <Chip
                  size="small"
                  icon={<ErrorIcon />}
                  label={severityCount.error}
                  color="error"
                  variant="outlined"
                />
              )}
              {severityCount.warning > 0 && (
                <Chip
                  size="small"
                  icon={<WarningIcon />}
                  label={severityCount.warning}
                  color="warning"
                  variant="outlined"
                />
              )}
              {severityCount.info > 0 && (
                <Chip
                  size="small"
                  icon={<InfoIcon />}
                  label={severityCount.info}
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {/* רשימת התראות */}
          {alerts.length === 0 ? (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 120,
              color: 'text.secondary'
            }}>
              <NotificationOffIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                אין התראות כרגע
              </Typography>
            </Box>
          ) : (
            <>
              <List dense sx={{ py: 0 }}>
                {visibleAlerts.map((alert, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      mb: 1,
                      border: 1,
                      borderColor: `${getAlertColor(alert.type)}.main`,
                      borderRadius: 1,
                      backgroundColor: `${getAlertColor(alert.type)}.50`,
                      '&:hover': {
                        backgroundColor: `${getAlertColor(alert.type)}.100`
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getAlertIcon(alert.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                          }}
                        >
                          {alert.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              mb: showTimestamp ? 0.5 : 0
                            }}
                          >
                            {alert.message}
                          </Typography>
                          {showTimestamp && alert.timestamp && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {formatTimestamp(alert.timestamp)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {/* כפתור הצג הכל */}
              {alerts.length > maxVisible && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Chip
                    label={showAll ? 'הצג פחות' : `הצג עוד (${alerts.length - maxVisible})`}
                    onClick={() => setShowAll(!showAll)}
                    variant="outlined"
                    size="small"
                    clickable
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default AlertsPanel;
